import { createMemo, createSignal, Show, type Component, type VoidComponent } from "solid-js";
import posthog from "posthog-js";
import { type InferOutput, object, picklist, array, pipe, nonOptional, custom } from "valibot";
import { email, requireText } from "../../../utils/validation/base";
import InputModal from "../../../components/modal/InputModal";
import type { LanguageCode } from "../../../state/i18n";
import { useTranslation } from "../../../state/i18n";
import {
  LANGUAGE_OPTIONS,
  MAX_CHARACTERS_MD,
  MAX_EXCEL_INTEGRATION_TEMPLATE_SIZE,
  MIME_TYPE_EXCEL,
} from "../../../constants";
import { SingleSelectInput } from "../../../components/menu/Select";
import UserService from "../../../services/UserService";
import { useCustomerState } from "../../../state/customer";
import MultiSelectWithSearch from "../../../components/menu/MultiSelectWithSearch";
import { resolveName } from "../../../utils/groupUtil";
import { groupAvatar } from "../../../utils/avatarUtil";
import { TextInput } from "../../../components/inputs/TextInput";
import { Header } from "../../../components/inputs/Input";
import { isEmpty, isNil } from "lodash-es";
import {
  getRoleName,
  groupRoles,
  orderRolesByName,
  removeAccountOwner,
  type GroupedRoles,
} from "../../../utils/roleUtil";
import { SingleSelectWithSearchInput } from "../../../components/menu/SingleSelectWithSearch";
import type { Role } from "../../../state/role";
import type { Group } from "../../../state/group";
import type { SelectOption } from "../../../types/Menu";
import { groupReference, roleReference } from "../../../utils/validation/idReferences";
import type RoleId from "../../../state/RoleId";
import type GroupId from "../../../state/GroupId";
import ResourceId from "../../../state/ResourceId";
import { useCurrentModal, useModalManager } from "../../../components/modal/ModalManager";
import GenericFailureModal from "../../GenericFailureModal";
import type { IconName } from "../../../components/icon/Icon";
import { translateFileUploadError } from "../../../utils/uploadUtil";
import UploadFileInput from "../../../components/inputs/UploadFileInput";
import type { FileError } from "@zag-js/file-utils";
import { toGrantDto } from "../../../utils/mappers/grant";
import { POSTHOG_EVENTS } from "../../../services/PostHogService";

const inviteSharedFields = {
  language: picklist(LANGUAGE_OPTIONS.map((o) => o.key)),
  role: roleReference(),
  groups: array(groupReference()),
};

const inviteUserSchema = object({
  email: pipe(requireText(MAX_CHARACTERS_MD), email(MAX_CHARACTERS_MD)),
  ...inviteSharedFields,
});

const inviteUsersSchema = object({
  excelFile: nonOptional(
    object({
      file: custom<File>((value) => value instanceof File),
      filename: requireText(MAX_CHARACTERS_MD),
      mediaTypeIcon: custom<IconName>((value) => typeof value === "string"),
    }),
  ),
  ...inviteSharedFields,
});

const useInviteState = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const groupState = UserService.getGroups(customer.customerId);
  const rolesState = UserService.getRoles(customer.customerId);

  const loadedState = createMemo(() => {
    if (isNil(groupState.data) || isNil(rolesState.data)) {
      return;
    }

    return { groups: groupState.data, roles: groupRoles(orderRolesByName(removeAccountOwner(rolesState.data), t)) };
  });

  const mapRoleToListItem = (role: Role): SelectOption<RoleId> => ({
    variant: "text",
    value: role.id,
    label: getRoleName(role, t),
  });

  return { loadedState, mapRoleToListItem };
};

type InviteSharedFieldsProps = {
  state: {
    groups: Group[];
    roles: GroupedRoles;
  };
  mapRoleToListItem: (role: Role) => SelectOption<RoleId>;
  language?: LanguageCode;
  role?: RoleId;
  groups?: GroupId[];
  onLanguageChange: (value: LanguageCode) => void;
  onRoleChange: (value: RoleId) => void;
  onGroupsChange: (value: GroupId[]) => void;
};

const InviteSharedFields: VoidComponent<InviteSharedFieldsProps> = (props) => {
  const { t } = useTranslation();
  return (
    <>
      <SingleSelectInput<LanguageCode>
        label={t(($) => $.USER_INVITE_MODAL_LANGUAGE)}
        value={props.language}
        options={LANGUAGE_OPTIONS.map(({ key, nativeLabel }) => ({
          variant: "text",
          value: key,
          label: nativeLabel,
        }))}
        onSelect={props.onLanguageChange}
        sameWidth
      />

      <SingleSelectWithSearchInput
        label={t(($) => $.USER_INVITE_MODAL_ROLE)}
        value={props.role}
        placeholder={t(($) => $.MENU_SEARCH_PLACEHOLDER_ROLE)}
        options={{
          builtin: [
            ...props.state.roles.accountSystemRoles.map(props.mapRoleToListItem),
            ...(!isEmpty(props.state.roles.accountCustomRoles)
              ? [
                  {
                    [t(($) => $.USER_INVITE_MODAL_ROLE_CUSTOM_TITLE)]: props.state.roles.accountCustomRoles.map(
                      props.mapRoleToListItem,
                    ),
                  },
                ]
              : []),
          ],
        }}
        onSelect={(roleId) => {
          if (!isNil(roleId)) {
            props.onRoleChange(roleId);
          }
        }}
        width="matchTrigger"
      />

      <div class="flex flex-col gap-1">
        <Header id="invite-user-groups" label={t(($) => $.USER_PAGE_ADD_TO_GROUP_LABEL)} />
        <MultiSelectWithSearch
          width="matchTrigger"
          options={{
            groups: props.state.groups.map((group) => ({
              variant: "avatar",
              ...groupAvatar(group),
              label: resolveName(group),
              value: group.id,
            })),
          }}
          placeholder={t(($) => $.MENU_SEARCH_PLACEHOLDER_GROUP)}
          value={props.groups}
          onSelect={props.onGroupsChange}
          id="invite-user-groups"
          ariaLabel={t(($) => $.USER_INVITE_MODAL_GROUPS_ARIA)}
        />
      </div>
    </>
  );
};

export const InviteUserModal: Component = () => {
  const { t, currentLanguage } = useTranslation();
  const { replace } = useModalManager();
  const { close } = useCurrentModal();
  const [duplicateEmailError, setDuplicateEmailError] = createSignal<boolean>(false);

  const customer = useCustomerState();
  const inviteUser = UserService.inviteUser(customer.customerId);
  const { loadedState, mapRoleToListItem } = useInviteState();

  const sendInvite = (values: InferOutput<typeof inviteUserSchema>) => {
    setDuplicateEmailError(false);

    inviteUser.mutate(
      {
        emailAddress: values.email,
        language: values.language,
        groups: values.groups,
        grants: [
          toGrantDto({
            customerId: customer.customerId,
            resourceType: "CUSTOMER",
            roleId: values.role,
            resourceId: ResourceId.from(customer.id.toString()),
          }),
        ],
      },
      {
        onSuccess: () => {
          posthog.capture(POSTHOG_EVENTS.USER_INVITED, { method: "single" });
          close();
        },
        onError: (err) =>
          err.code === "user_already_exists_for_customer"
            ? setDuplicateEmailError(true)
            : replace({
                content: () => <GenericFailureModal error={err} />,
              }),
      },
    );
  };

  return (
    <Show when={loadedState()}>
      {(state) => (
        <InputModal
          variant="default"
          size="large"
          title={t(($) => $.USER_INVITE_MODAL_TITLE)}
          titleIcon="user"
          confirmLabel={t(($) => $.USER_INVITE_MODAL_CONFIRM_LABEL)}
          cancelLabel={t(($) => $.USER_INVITE_MODAL_CANCEL_LABEL)}
          onConfirm={sendInvite}
          schema={inviteUserSchema}
          closeOnSubmit={false}
          initialValue={{
            email: undefined,
            language: currentLanguage(),
            groups: [],
            role: state().roles.accountSystemRoles[0]?.id,
          }}
        >
          {({ value, updateValue, error }) => (
            <div class="flex flex-col gap-4">
              <TextInput
                required
                label={t(($) => $.USER_INVITE_MODAL_EMAIL_TITLE)}
                value={value("email")}
                onBlur={(e) => updateValue("email", e.target.value)}
                onInput={(e) => updateValue("email", e.target.value)}
                errorMessage={
                  error("email") ??
                  (duplicateEmailError() ? t(($) => $.USER_INVITE_MODAL_EMAIL_EXISTS_ERROR) : undefined)
                }
              />
              <InviteSharedFields
                state={state()}
                mapRoleToListItem={mapRoleToListItem}
                language={value("language")}
                role={value("role")}
                groups={value("groups")}
                onLanguageChange={(v) => updateValue("language", v)}
                onRoleChange={(v) => updateValue("role", v)}
                onGroupsChange={(v) => updateValue("groups", v)}
              />
            </div>
          )}
        </InputModal>
      )}
    </Show>
  );
};

export const InviteUsersModal: Component = () => {
  const { t, currentLanguage } = useTranslation();
  const { close } = useCurrentModal();
  const { replace } = useModalManager();

  const customer = useCustomerState();
  const inviteUsers = UserService.inviteUsers(customer.customerId);
  const { loadedState, mapRoleToListItem } = useInviteState();

  const [fileError, setFileError] = createSignal<FileError | undefined>();
  const errorMessage = createMemo(() => {
    const error = fileError();
    return !isNil(error) ? translateFileUploadError(error, MAX_EXCEL_INTEGRATION_TEMPLATE_SIZE) : undefined;
  });

  const sendInvite = (values: InferOutput<typeof inviteUsersSchema>) => {
    const formData = new FormData();
    formData.append("language", values.language);
    formData.append("roleId", values.role.toString());
    formData.append("groups", values.groups.map((group) => group.toString()).join(","));
    formData.append("file", values.excelFile.file);

    return new Promise<void>((resolve, reject) => {
      inviteUsers.mutate(formData, {
        onSuccess: () => {
          posthog.capture(POSTHOG_EVENTS.USER_INVITED, { method: "bulk" });
          close();
          resolve();
        },
        onError: (err) => {
          replace({
            content: () => <GenericFailureModal error={err} />,
          });
          reject(err);
        },
      });
    });
  };

  return (
    <Show when={loadedState()}>
      {(state) => (
        <InputModal
          variant="default"
          size="large"
          title={t(($) => $.USERS_INVITE_MODAL_TITLE)}
          titleIcon="users"
          confirmLabel={t(($) => $.USER_INVITE_MODAL_CONFIRM_LABEL)}
          cancelLabel={t(($) => $.USER_INVITE_MODAL_CANCEL_LABEL)}
          onConfirm={sendInvite}
          schema={inviteUsersSchema}
          closeOnSubmit={false}
          initialValue={{
            excelFile: undefined,
            language: currentLanguage(),
            groups: [],
            role: state().roles.accountSystemRoles[0]?.id,
          }}
        >
          {({ value, updateValue, error }) => (
            <div class="flex flex-col gap-4">
              <UploadFileInput
                label={t(($) => $.USERS_INVITE_MODAL_USERS_TITLE)}
                value={value("excelFile")}
                maxFileSizeInBytes={MAX_EXCEL_INTEGRATION_TEMPLATE_SIZE}
                accept={[MIME_TYPE_EXCEL]}
                required
                uploadActionLabel={t(($) => $.USERS_INVITE_MODAL_EXCEL_TITLE)}
                info={{
                  title: t(($) => $.USERS_INVITE_MODAL_EXCEL_HELP_TITLE),
                  alt: t(($) => $.USERS_INVITE_MODAL_EXCEL_HELP_ARIA),
                  description: t(($) => $.USERS_INVITE_MODAL_EXCEL_HELP_DESCRIPTION),
                }}
                errorMessage={error("excelFile") ?? errorMessage()}
                setError={setFileError}
                receiveFile={(file) => {
                  if (!isNil(file)) {
                    updateValue("excelFile", { file, filename: file.name, mediaTypeIcon: "excelLogo" });
                  } else {
                    updateValue("excelFile", undefined);
                  }
                }}
              />
              <InviteSharedFields
                state={state()}
                mapRoleToListItem={mapRoleToListItem}
                language={value("language")}
                role={value("role")}
                groups={value("groups")}
                onLanguageChange={(value) => updateValue("language", value)}
                onRoleChange={(value) => updateValue("role", value)}
                onGroupsChange={(value) => updateValue("groups", value)}
              />
            </div>
          )}
        </InputModal>
      )}
    </Show>
  );
};
